import { GasStationClient, GasStationTransactionSubmitter } from "@aptos-labs/gas-station-client";
import { Aptos, AptosConfig, Network } from "@aptos-labs/ts-sdk";
import type { DappConfig } from "@aptos-labs/wallet-adapter-core";

import { APTOS_COIN_INTERNAL, GAS_STATION_API_KEY } from "@constants";
import { TOKEN_LOGOS } from "@constants/logos";
import { TOKEN_METADATA } from "@constants/metadata";
import { FAUCET_APT } from "@constants/tokens";

type NetworkName = "testnet" | "mainnet" | "devnet" | "local";

export const getNetworkFromName = (name: string): Network => {
  const networkMap: Record<NetworkName, Network> = {
    testnet: Network.TESTNET,
    mainnet: Network.MAINNET,
    devnet: Network.DEVNET,
    local: Network.LOCAL,
  };

  return networkMap[name as NetworkName] ?? Network.TESTNET;
};

// Initial values (will be updated by ConfigProvider)
let currentNetwork = getNetworkFromName(import.meta.env.VITE_APTOS_NETWORK || "testnet");
let currentApiKey = import.meta.env.VITE_APTOS_API_KEY || "";

const createAptosConfig = (network: Network, apiKey: string) =>
  new AptosConfig({
    network,
    clientConfig: apiKey ? { API_KEY: apiKey } : undefined,
  });

export let aptosConfig = createAptosConfig(currentNetwork, currentApiKey);

export let aptosClient = new Aptos(aptosConfig);

export const updateAptosClient = (network: string, apiKey: string) => {
  currentNetwork = getNetworkFromName(network);
  currentApiKey = apiKey;

  aptosConfig = createAptosConfig(currentNetwork, currentApiKey);
  aptosClient = new Aptos(aptosConfig);

  // Update legacy exports
  aptosNetwork = currentNetwork;
  isAptosTestnet = currentNetwork === Network.TESTNET;
  isAptosMainnet = currentNetwork === Network.MAINNET;
  isAptosDevnet = currentNetwork === Network.DEVNET;
  isAptosLocal = currentNetwork === Network.LOCAL;
};

export const getAptosDappConfig = (network: string, apiKey: string): DappConfig => {
  const network_ = getNetworkFromName(network);
  const aptosConnectDappId = import.meta.env.VITE_APTOS_CONNECT_DAPP_ID;
  const dappImageURI = typeof window !== "undefined" ? `${window.location.origin}/favicon.ico` : undefined;
  const dappName = typeof document !== "undefined" ? document.title : "Ekiden";
  let transactionSubmitter: GasStationTransactionSubmitter | undefined = undefined;
  if (GAS_STATION_API_KEY) {
    const gasStationClient = new GasStationClient({
      network: network_,
      apiKey: GAS_STATION_API_KEY,
    });
    transactionSubmitter = new GasStationTransactionSubmitter(gasStationClient);
  }
  return {
    network: network_,
    aptosApiKeys: {
      [Network.TESTNET]: apiKey,
      [Network.MAINNET]: apiKey,
      [Network.DEVNET]: apiKey,
    },
    aptosConnect: {
      ...(aptosConnectDappId ? { dappId: aptosConnectDappId } : {}),
      dappName,
      dappImageURI,
    },
    crossChainWallets: true,
    transactionSubmitter,
  };
};

export interface TokenMetadata {
  name: string;
  symbol: string;
  icon: string;
  decimals: number;
}

// Keep these for backward compatibility where possible, but they might be stale
// Better to use the reactive stores/config where possible
export let aptosNetwork = currentNetwork;
export let isAptosTestnet = currentNetwork === Network.TESTNET;
export let isAptosMainnet = currentNetwork === Network.MAINNET;
export let isAptosDevnet = currentNetwork === Network.DEVNET;
export let isAptosLocal = currentNetwork === Network.LOCAL;

export const getTokenMetadata = async (address: string): Promise<TokenMetadata | null> => {
  const metadata = TOKEN_METADATA[address];

  if (!metadata) {
    return null;
  }

  return {
    name: metadata.name ?? "",
    symbol: metadata.symbol ?? "",
    icon: metadata.icon ?? TOKEN_LOGOS.UNKNOWN,
    decimals: 8,
  };
};

export const getMultipleTokenMetadata = async (addresses: string[]): Promise<Record<string, TokenMetadata | null>> => {
  const results: Record<string, TokenMetadata | null> = {};

  const promises = addresses.map(async (address) => {
    return getTokenMetadata(address);
  });

  const settled = await Promise.all(promises);

  settled.forEach((result, index) => {
    const address = addresses[index];

    // We search for Aptos using the 0xa address,
    // but internally we use the full address
    if (address === FAUCET_APT) {
      results[APTOS_COIN_INTERNAL] = result;
      return;
    }

    results[address] = result;
  });

  return results;
};
